// --------------------------------------------------------------
// Created On: 2025-04-09
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import QuickOrderListItemForm from "../QuickOrderListItemForm/QuickOrderListItemForm";
import styles from "../QuickOrderPage.module.scss";

// This component is used to display a list of parts in the Quick Order page.
export default function QuickOrderList(props: Props): Component {
  return (
    <Fragment>
      {props.partsList.map((part, i) => (
        <div className={styles.body} key={i}>
          <QuickOrderListItemForm
            loading={props.loading}
            partFound={props.partFound}
            partNumberInvalid={part.partNumberIsInvalid}
            errorMessage={part.invalidPartNumberMessage || props.errorMessage}
            inputValue={props.inputValue}
            lineItemNumber={i + 1}
            partId={part.partId}
            partNumber={part.partNumber}
            description={part.description}
            unitName={part.unitName}
            quantity={part.quantity}
            price={part.price}
            companyDiscount={part.companyDiscount}
            weight={part.weight}
            isPaintable={part.isPaintable}
            isImpeller={part.isImpeller}
            isFullTrim={part.isFullTrim}
            trimOption={part.impellerTrimOption}
            trim={part.impellerTrimValue}
            angle={part.impellerAngle}
            isSellable={part.isSellable}
            paintColors={props.paintColors}
            selectedPaintColorId={part.isPaintable ? part.paintColorId : null}
            searchComplete={props.searchComplete}
            onLoadingChange={(loading) => props.onLoadingChange(loading)}
            onErrorMessageChange={(errorMessage) => props.onErrorMessageChange(errorMessage)}
            onInputValueChange={(inputValue) => props.onInputValueChange(inputValue)}
            onPartNumberChange={(partNumber) => props.onPartNumberChange(i, partNumber)}
            onQuantityChange={(quantity) => props.onQuantityChange(i, quantity)}
            onPaintColorChange={(colorId) => props.onPaintColorChange(i, colorId)}
            onTrimValueChange={(trimValue) => props.onTrimValueChange(i, trimValue)}
            onTrimOptionChange={(trim) => props.onTrimOptionChange(i, trim)}
            onAngleChange={(angle) => props.onAngleChange(i, angle)}
            onDeletePart={(lineItemNumber) => props.onDeletePart(lineItemNumber)}
            onPartNumberSearch={(partNumber) => props.onPartNumberSearch(i, partNumber)}
            onSearchComplete={(complete) => props.onSearchComplete(complete)}
            onPerformSearch={() => props.onPerformSearch()}
          />
        </div>
      ))}
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  partFound: boolean;
  errorMessage: string;
  inputValue: string;
  searchComplete: boolean;
  paintColors: PaintColor[];
  partsList: Part[];
  onSearchComplete: (complete: boolean) => void;
  onLoadingChange: (loading: boolean) => void;
  onErrorMessageChange: (errorMessage: string) => void;
  onInputValueChange: (inputValue: string) => void;
  onPartNumberChange: (index: number, partNumber: string) => void;
  onQuantityChange: (index: number, quantity: string) => void;
  onTrimValueChange: (index: number, trim: string) => void;
  onTrimOptionChange: (index: number, trim: string) => void;
  onAngleChange: (index: number, angle: string) => void;
  onPaintColorChange: (index: number, colorId: number | null) => void;
  onPartNumberSearch: (index: number, partNumber: string) => void;
  onPerformSearch: () => void;
  onDeletePart: (partId: number) => void;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface Part {
  partId: number;
  partNumber: string;
  description: string;
  quantity: string;
  unitName: string;
  price: number;
  companyDiscount: number | null;
  weight: number | null;
  paintColorId: number | null;
  partNumberIsInvalid: boolean;
  isSellable: boolean;
  isPaintable: boolean;
  isImpeller: boolean;
  isFullTrim: boolean;
  impellerTrimOption: string;
  impellerTrimValue: string | null;
  impellerAngle: string | null;
  invalidPartNumberMessage: string;
}
